import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  Input,
  Alert,
  FormGroup,
  Label,
  InputGroup,
  InputGroupText
} from 'reactstrap';
import { getStockByProduct } from './Api/GmsStock';
import { 
  getStockSelectionLocks, 
  insertStockSelectionLock, 
  deleteStockSelectionLock 
} from './Api/GmsStockSelectionLock';
import { Search, Package } from 'lucide-react';

const StockSelectionModal = ({ 
  isOpen, 
  toggle, 
  productId, 
  productName, 
  requestedQuantity, 
  onStockSelected,
  selectedStockItems = [],
  branchId = null,
  businessId = null,  // NEW PROP
  preloadedStockItems = null,  // NEW: For purchase return - pre-loaded stock items
  showCostPriceColumn = false,  // NEW: Show cost price column (for stock transfer)
  customFetchStock = null,  // NEW: Custom function to fetch stock (for stock transfer)
  temporaryFormUniqueId = null,  // NEW: Unique form identifier for lock management
  flag = null,  // NEW: Form type flag (3=Purchase Return, 5=Transfer, 7=Sales, 8=Sales Return)
  createdBy = null  // NEW: User ID for lock creation
}) => {
  const [stockItems, setStockItems] = useState([]);
  const [filteredStockItems, setFilteredStockItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [displayCount, setDisplayCount] = useState(0);
  const [showMore, setShowMore] = useState(false);
  const [lockedStockIds, setLockedStockIds] = useState([]);  // NEW: Track locked stock IDs
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());  // Track dismissed alert IDs
  const [lockedStocksLoaded, setLockedStocksLoaded] = useState(false);  // Track if locked stocks have been loaded
  
  // NEW: Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize] = useState(50);  // Fixed page size
  const [hasMorePages, setHasMorePages] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Calculate initial display count (5x requested quantity)
  const initialDisplayCount = Math.max(10, requestedQuantity * 5);

  // Helper function to parse DD/MM/YYYY format date string
  const parseDDMMYYYY = (dateStr) => {
    if (!dateStr) return null;
    // Check if it's already in DD/MM/YYYY format
    const ddMMyyyyPattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dateStr.match(ddMMyyyyPattern);
    if (match) {
      const [, day, month, year] = match;
      // Create date in YYYY-MM-DD format for proper parsing
      return new Date(`${year}-${month}-${day}`);
    }
    // Try standard Date parsing for other formats
    const parsed = new Date(dateStr);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  // Fetch locked stocks to filter them out
  const fetchLockedStocks = async () => {
    try {
      // Pass null for flag to get ALL locked stocks regardless of form type
      // This ensures we see locks from Sales Invoice, Stock Transfer, Purchase Return, etc.
      const response = await getStockSelectionLocks(productId, temporaryFormUniqueId, null);
      if (response.isSuccess && response.lockedStocks) {
        const lockedIds = response.lockedStocks.map(lock => lock.purchaseStock_Id);
        setLockedStockIds(lockedIds);
        setLockedStocksLoaded(true);
      } else {
        setLockedStockIds([]);
        setLockedStocksLoaded(true);
      }
    } catch (error) {
      console.error('❌ Error fetching locked stocks:', error);
      setLockedStockIds([]);
      setLockedStocksLoaded(true);
      // Don't show error to user, just continue without filtering
    }
  };

  useEffect(() => {
    if (isOpen && productId) {
      // Reset state immediately when modal opens or productId changes
      // This prevents showing stale data from previous product
      setSelectedItems([]);
      setStockItems([]);
      setFilteredStockItems([]);
      setLockedStockIds([]);
      setAlerts([]);
      setDismissedAlerts(new Set());
      setLockedStocksLoaded(false);
      setLoading(true); // Show loader immediately
      setSearchTerm(''); // Clear search term
      setDisplayCount(initialDisplayCount);
      // NEW: Reset pagination state
      setCurrentPage(1);
      setTotalPages(1);
      setTotalRecords(0);
      setHasMorePages(false);
      setIsLoadingMore(false);
      
      // IMPORTANT: Fetch locked stocks FIRST, then load stock items
      // This ensures locked stocks are known before auto-selection happens
      const initializeModal = async () => {
        let currentLockedIds = [];
        
        if (temporaryFormUniqueId && flag) {
          // Fetch locked stocks and wait for the state to update
          // Pass null for flag to get ALL locked stocks regardless of form type
          const response = await getStockSelectionLocks(productId, temporaryFormUniqueId, null);
          if (response.isSuccess && response.lockedStocks) {
            currentLockedIds = response.lockedStocks.map(lock => lock.purchaseStock_Id);
            setLockedStockIds(currentLockedIds);
            setLockedStocksLoaded(true);
          } else {
            setLockedStockIds([]);
            setLockedStocksLoaded(true);
          }
        } else {
          console.warn('⚠️ Lock feature not enabled - missing props:', { temporaryFormUniqueId, flag });
          setLockedStocksLoaded(true); // Set to true so auto-selection can proceed
        }
        
        // Now load stock items after locked stocks are fetched
        if (preloadedStockItems) {
          // Use pre-loaded stock items (for purchase return)
          // Filter out locked stocks from preloaded items using currentLockedIds
          const unlockedPreloadedItems = preloadedStockItems.filter(item => 
            !currentLockedIds.includes(item.purchaseStock_Id)
          );
          setStockItems(unlockedPreloadedItems);
          setDisplayCount(initialDisplayCount);
          setLoading(false);
        } else {
          // Fetch stock from API (for regular usage) - First page only
          // Pass currentLockedIds to filter function
          await fetchStockDataWithLocks(currentLockedIds, 1, null, false);
        }
      };
      
      initializeModal();
    } else if (!isOpen) {
      // Reset everything when modal closes
      setSelectedItems([]);
      setStockItems([]);
      setFilteredStockItems([]);
      setLockedStockIds([]);
      setAlerts([]);
      setDismissedAlerts(new Set());
      setLockedStocksLoaded(false);
      setLoading(false);
      setSearchTerm('');
      // NEW: Reset pagination state
      setCurrentPage(1);
      setTotalPages(1);
      setTotalRecords(0);
      setHasMorePages(false);
      setIsLoadingMore(false);
    }
  }, [isOpen, productId, preloadedStockItems, temporaryFormUniqueId, flag]);

  // Periodically refresh locked stocks while modal is open (every 5 seconds)
  useEffect(() => {
    if (!isOpen || !temporaryFormUniqueId || !flag) return;
    
    // Initial fetch
    fetchLockedStocks();
    
    // Then refresh every 5 seconds
    const intervalId = setInterval(() => {
      fetchLockedStocks();
    }, 5000); // Refresh every 5 seconds
    
    return () => clearInterval(intervalId);
  }, [isOpen, temporaryFormUniqueId, flag, productId]);

  // Cleanup locks when modal closes (cancel)
  useEffect(() => {
    return () => {
      // Cleanup locks when component unmounts or modal closes
      if (temporaryFormUniqueId && !isOpen) {
        handleCleanupLocks();
      }
    };
  }, [isOpen, temporaryFormUniqueId]);

  useEffect(() => {
    // Filter stock items based on locked stocks only (search is now server-side)
    // Note: When search is active, we fetch from server, so this only filters locked stocks
    const filtered = stockItems.filter(item => {
      const isNotLocked = !lockedStockIds.includes(item.purchaseStock_Id);
      return isNotLocked;
    });
    setFilteredStockItems(filtered);
  }, [stockItems, lockedStockIds]);

  // NEW: Handle server-side search with debounce
  useEffect(() => {
    if (!isOpen || !productId || preloadedStockItems || !lockedStocksLoaded) return; // Skip if preloaded, modal closed, or locks not loaded yet
    
    // Only trigger search if user has actually typed something (not on initial load)
    // Initial load is handled by initializeModal, so we only want to search when searchTerm changes from user input
    if (searchTerm === '') {
      // If search is cleared and we have items, don't refetch (user might have cleared search)
      return;
    }
    
    // Debounce search to avoid too many API calls
    const searchTimeout = setTimeout(() => {
      // Reset to page 1 when searching
      setCurrentPage(1);
      setStockItems([]);
      setFilteredStockItems([]);
      fetchStockDataWithLocks(lockedStockIds, 1, searchTerm);
    }, 500); // 500ms debounce

    return () => clearTimeout(searchTimeout);
  }, [searchTerm, isOpen, productId, preloadedStockItems, lockedStocksLoaded]);

  useEffect(() => {
    // Auto-select items based on requested quantity and expiry date
    // IMPORTANT: Only auto-select AFTER locked stocks have been loaded to avoid selecting locked stocks
    if (filteredStockItems.length > 0 && selectedItems.length === 0 && requestedQuantity > 0 && lockedStocksLoaded) {
      // filteredStockItems is already filtered to exclude locked stocks, so we can safely auto-select
      const sortedItems = [...filteredStockItems].sort((a, b) => {
        // Sort by expiry date (earliest first)
        if (a.purchaseStock_ExpDate && b.purchaseStock_ExpDate) {
          const dateA = parseDDMMYYYY(a.purchaseStock_ExpDate);
          const dateB = parseDDMMYYYY(b.purchaseStock_ExpDate);
          if (dateA && dateB) {
            return dateA - dateB;
          }
          if (dateA) return -1;
          if (dateB) return 1;
        }
        if (a.purchaseStock_ExpDate) return -1;
        if (b.purchaseStock_ExpDate) return 1;
        return 0;
      });

      const autoSelected = sortedItems.slice(0, requestedQuantity);
      const autoSelectedIds = autoSelected.map(item => item.purchaseStock_Id);
      setSelectedItems(autoSelectedIds);
      
      // Insert locks for auto-selected stocks if lock feature is enabled
      if (temporaryFormUniqueId && flag && createdBy && autoSelectedIds.length > 0) {
        // Insert locks for all auto-selected stocks
        const insertLocksForAutoSelected = async () => {
          try {
            const stockIdsString = autoSelectedIds.join(',');
            const result = await insertStockSelectionLock(
              stockIdsString,
              temporaryFormUniqueId,
              flag,
              createdBy
            );
            
            if (!result.isSuccess) {
              console.warn('⚠️ Some auto-selected stocks could not be locked:', result.message);
              // Remove stocks that couldn't be locked from selection
              // Note: The API should handle partial failures, but we'll keep all selected
              // and let the user see the error if any
              if (result.message) {
                const alertId = `auto-lock-warning-${Date.now()}-${Math.random()}`;
                setAlerts(prev => {
                  const hasSimilarAlert = prev.some(alert => 
                    alert.message.includes('Some stocks could not be locked') && alert.type === 'warning'
                  );
                  if (!hasSimilarAlert) {
                    // Auto-dismiss after 5 seconds
                    setTimeout(() => {
                      setDismissedAlerts(prev => new Set([...prev, alertId]));
                      setAlerts(current => current.filter(alert => alert.id !== alertId));
                    }, 5000);
                    return [...prev, {
                      type: 'warning',
                      message: `Some stocks could not be locked: ${result.message}`,
                      id: alertId
                    }];
                  }
                  return prev;
                });
              }
            }
          } catch (error) {
            console.error('❌ Error locking auto-selected stocks:', error);
            // Don't remove from selection, but show warning
            const alertId = `auto-lock-error-${Date.now()}-${Math.random()}`;
            setAlerts(prev => {
              const hasSimilarAlert = prev.some(alert => 
                alert.message.includes('could not be locked automatically') && alert.type === 'warning'
              );
              if (!hasSimilarAlert) {
                // Auto-dismiss after 5 seconds
                setTimeout(() => {
                  setDismissedAlerts(prev => new Set([...prev, alertId]));
                  setAlerts(current => current.filter(alert => alert.id !== alertId));
                }, 5000);
                return [...prev, {
                  type: 'warning',
                  message: 'Some stocks could not be locked automatically. Please verify availability.',
                  id: alertId
                }];
              }
              return prev;
            });
          }
        };
        
        insertLocksForAutoSelected();
      }
    }
  }, [filteredStockItems, requestedQuantity, temporaryFormUniqueId, flag, createdBy, lockedStocksLoaded]);

  // Helper function to fetch stock data with locked stock IDs passed as parameter
  // UPDATED: Now supports pagination and server-side search
  const fetchStockDataWithLocks = async (lockedIds = [], page = 1, batchSearch = null, append = false) => {
    if (!append) {
      setLoading(true);
    } else {
      setIsLoadingMore(true);
    }
    setAlerts([]);
    try {
      // Use custom fetch function if provided (for stock transfer), otherwise use default with pagination
      const response = customFetchStock 
        ? await customFetchStock(productId, branchId, businessId)
        : await getStockByProduct(productId, businessId, branchId, page, pageSize, batchSearch);
      
      if (response.success) {
        const fetchedStockItems = response.stockItems || [];
        
        // Filter out locked stocks immediately when setting stock items
        const unlockedStockItems = fetchedStockItems.filter(item => 
          !lockedIds.includes(item.purchaseStock_Id)
        );
        
        // NEW: Append to existing items if loading more, otherwise replace
        if (append) {
          setStockItems(prev => {
            // Check for duplicates before appending (in case same page is fetched twice)
            const existingIds = new Set(prev.map(item => item.purchaseStock_Id));
            const newUniqueItems = unlockedStockItems.filter(item => !existingIds.has(item.purchaseStock_Id));
            const newItems = [...prev, ...newUniqueItems];
            return newItems;
          });
          // Update display count to show all loaded items when appending
          setDisplayCount(prev => prev + unlockedStockItems.length);
        } else {
          setStockItems(unlockedStockItems);
          setDisplayCount(Math.min(initialDisplayCount, unlockedStockItems.length));
        }
        
        // NEW: Update pagination state
        const totalPagesValue = response.totalPages || 1;
        const totalRecordsValue = response.totalRecords || response.totalAvailableStock || 0;
        setCurrentPage(page);
        setTotalPages(totalPagesValue);
        setTotalRecords(totalRecordsValue);
        setHasMorePages(page < totalPagesValue);
        
        // Set up alerts based on stock status (only on first page load)
        if (!append) {
          const newAlerts = [];
          if (response.hasExpiredStock) {
            newAlerts.push({
              type: 'warning',
              message: 'Some stock items have expired. Please check expiry dates carefully.'
            });
          }
          if (response.hasCloseToExpiryStock) {
            newAlerts.push({
              type: 'info',
              message: 'Some stock items are close to expiry (within 30 days).'
            });
          }
          if (response.totalAvailableStock < requestedQuantity) {
            newAlerts.push({
              type: 'danger',
              message: `Only ${response.totalAvailableStock} stock items available, but ${requestedQuantity} requested.`
            });
          }
          setAlerts(newAlerts);
        }
      } else {
        if (!append) {
          setStockItems([]);
          setAlerts([{ type: 'info', message: response.message || 'No stock available' }]);
        }
      }
    } catch (error) {
      console.error('Error fetching stock:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Error loading stock data';
      if (!append) {
        setAlerts([{ type: 'danger', message: errorMessage }]);
      }
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Original fetchStockData for backward compatibility (used by periodic refresh)
  const fetchStockData = async () => {
    await fetchStockDataWithLocks(lockedStockIds, currentPage, searchTerm || null, false);
  };

  const handleItemToggle = async (stockId) => {
    const isCurrentlySelected = selectedItems.includes(stockId);
    
    console.log('🔒 Stock toggle:', {
      stockId,
      isCurrentlySelected,
      temporaryFormUniqueId,
      flag,
      createdBy,
      hasAllProps: !!(temporaryFormUniqueId && flag && createdBy)
    });
    
    if (isCurrentlySelected) {
      // Deselecting - remove from selected items
      setSelectedItems(prev => prev.filter(id => id !== stockId));
      
      // Delete lock for this stock if lock feature is enabled
      if (temporaryFormUniqueId && flag && createdBy) {
        try {
          console.log('🔒 Deleting lock for stock:', stockId);
          await deleteStockSelectionLock(temporaryFormUniqueId, stockId.toString());
          console.log('✅ Lock deleted successfully');
        } catch (error) {
          console.error('❌ Error deleting stock lock:', error);
        }
      } else {
        console.warn('⚠️ Lock deletion skipped - missing props:', { temporaryFormUniqueId, flag, createdBy });
      }
    } else {
      // Selecting - add to selected items
      setSelectedItems(prev => [...prev, stockId]);
      
      // Insert lock for this stock if lock feature is enabled
      if (temporaryFormUniqueId && flag && createdBy) {
        try {
          console.log('🔒 Inserting lock for stock:', {
            stockId,
            temporaryFormUniqueId,
            flag,
            createdBy
          });
          const result = await insertStockSelectionLock(
            stockId.toString(),
            temporaryFormUniqueId,
            flag,
            createdBy
          );
          
          console.log('🔒 Lock insert result:', result);
          
          if (!result.isSuccess) {
            // If lock failed (stock already locked), remove from selection
            setSelectedItems(prev => prev.filter(id => id !== stockId));
            
            // Immediately hide the stock by adding it to lockedStockIds
            setLockedStockIds(prev => {
              if (!prev.includes(stockId)) {
                return [...prev, stockId];
              }
              return prev;
            });
            
            // Show alert only if we don't already have a similar alert
            const errorMessage = result.message || 'This stock is already locked by another user';
            setAlerts(prev => {
              // Check if we already have this exact error message
              const hasSimilarAlert = prev.some(alert => 
                alert.message === errorMessage && alert.type === 'danger'
              );
              
              if (!hasSimilarAlert) {
                const newAlert = {
                  type: 'danger',
                  message: errorMessage,
                  id: `lock-failed-${Date.now()}-${Math.random()}` // Unique ID for dismissal
                };
                
                // Auto-dismiss after 5 seconds
                setTimeout(() => {
                  setDismissedAlerts(prev => new Set([...prev, newAlert.id]));
                  setAlerts(current => current.filter(alert => alert.id !== newAlert.id));
                }, 5000);
                
                return [...prev, newAlert];
              }
              return prev;
            });
            
            // Refresh locked stocks list to get latest state
            await fetchLockedStocks();
          } else {
            console.log('✅ Lock inserted successfully');
          }
        } catch (error) {
          console.error('❌ Error inserting stock lock:', error);
          // Remove from selection if lock failed
          setSelectedItems(prev => prev.filter(id => id !== stockId));
          
          // Immediately hide the stock by adding it to lockedStockIds
          setLockedStockIds(prev => {
            if (!prev.includes(stockId)) {
              return [...prev, stockId];
            }
            return prev;
          });
          
          // Show alert only if we don't already have a similar alert
          const errorMessage = 'Failed to lock stock. It may be locked by another user.';
          setAlerts(prev => {
            // Check if we already have this exact error message
            const hasSimilarAlert = prev.some(alert => 
              alert.message === errorMessage && alert.type === 'danger'
            );
            
            if (!hasSimilarAlert) {
              const newAlert = {
                type: 'danger',
                message: errorMessage,
                id: `lock-error-${Date.now()}-${Math.random()}` // Unique ID for dismissal
              };
              
              // Auto-dismiss after 5 seconds
              setTimeout(() => {
                setDismissedAlerts(prev => new Set([...prev, newAlert.id]));
                setAlerts(current => current.filter(alert => alert.id !== newAlert.id));
              }, 5000);
              
              return [...prev, newAlert];
            }
            return prev;
          });
          
          // Refresh locked stocks list
          await fetchLockedStocks();
        }
      } else {
        console.warn('⚠️ Lock insertion skipped - missing props:', { temporaryFormUniqueId, flag, createdBy });
      }
    }
  };

  // UPDATED: Fetch next page from server instead of showing more from loaded data
  const handleShowMore = async () => {
    if (hasMorePages && !isLoadingMore) {
      const nextPage = currentPage + 1;
      await fetchStockDataWithLocks(lockedStockIds, nextPage, searchTerm || null, true);
    }
  };

  const handleConfirm = () => {
    const selectedStockData = stockItems.filter(item => 
      selectedItems.includes(item.purchaseStock_Id)
    );
    
    // Group by sales price AND discount percentage
    const groupedStock = {};
    selectedStockData.forEach(stock => {
      const price = stock.purchaseStock_SalesPrice || 0;
      const discount = stock.discountPercent || 0;
      const key = `${price}_${discount}`; // Group by both price and discount
      
      if (!groupedStock[key]) {
        groupedStock[key] = [];
      }
      groupedStock[key].push(stock);
    });

    // Convert to final format
    const finalStockData = Object.entries(groupedStock).map(([key, stocks]) => {
      const [price, discount] = key.split('_');
      return {
        price: parseFloat(price),
        discount: parseFloat(discount), // Include discount in final data
        quantity: stocks.length,
        stocks: stocks,
        batchNumbers: stocks.map(s => s.purchaseStock_BatchNo).join(', '),
        expiryDates: stocks.map(s => s.purchaseStock_ExpDate).join(', ')
      };
    });

    onStockSelected(finalStockData);
    // Don't cleanup locks here - let parent handle cleanup after successful save
    toggle();
  };

  // Cleanup locks when modal is cancelled
  const handleCleanupLocks = async () => {
    if (temporaryFormUniqueId && selectedItems.length > 0) {
      try {
        // Delete locks for selected stocks only
        const stockIdsString = selectedItems.join(',');
        await deleteStockSelectionLock(temporaryFormUniqueId, stockIdsString);
      } catch (error) {
        console.error('Error cleaning up locks on cancel:', error);
      }
    }
  };

  // Handle cancel button click
  const handleCancel = () => {
    handleCleanupLocks();
    toggle();
  };

  const getDisplayItems = () => {
    // Show all filtered items (no limit when using pagination)
    // The pagination is handled server-side, so we show all items we've loaded
    return filteredStockItems;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const dateObj = parseDDMMYYYY(dateStr);
      if (!dateObj) return dateStr; // Return original string if parsing failed
      return dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const isExpired = (expDate) => {
    if (!expDate) return false;
    const dateObj = parseDDMMYYYY(expDate);
    if (!dateObj) return false;
    return dateObj < new Date();
  };

  const isCloseToExpiry = (expDate) => {
    if (!expDate) return false;
    const dateObj = parseDDMMYYYY(expDate);
    if (!dateObj) return false;
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(thirtyDaysFromNow.getDate() + 30);
    return dateObj <= thirtyDaysFromNow && dateObj >= new Date();
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <Package className="me-2" size={20} />
        Stock Selection - {productName}
      </ModalHeader>
      <style>{`
        .stock-selection-modal .badge,
        .stock-selection-modal .badge.bg-danger,
        .stock-selection-modal .badge.bg-warning,
        .stock-selection-modal .badge.bg-success,
        .stock-selection-modal span.badge,
        .stock-selection-modal td .badge {
          font-size: 0.875rem !important;
          line-height: 1.2 !important;
          padding: 0.25em 0.4em !important;
        }
        .stock-selection-modal table,
        .stock-selection-modal table th,
        .stock-selection-modal table td,
        .stock-selection-modal table tbody,
        .stock-selection-modal table thead,
        .stock-selection-modal table td span.badge {
          font-size: 0.875rem !important;
        }
      `}</style>
      <ModalBody className="stock-selection-modal" style={{ fontSize: '0.875rem' }}>
        {/* Alerts - Filter out dismissed alerts */}
        {alerts
          .filter(alert => !dismissedAlerts.has(alert.id))
          .map((alert, index) => (
            <Alert 
              key={alert.id || index} 
              color={alert.type} 
              className="mb-3"
              fade={true}
              style={{ fontSize: '0.875rem' }}
            >
              {alert.message}
            </Alert>
          ))}

        {/* Search */}
        <FormGroup>
          <Label for="batchSearch" style={{ fontSize: '0.875rem' }}>Search by Batch Number</Label>
          <InputGroup>
            <InputGroupText>
              <Search size={16} />
            </InputGroupText>
            <Input
              id="batchSearch"
              type="text"
              placeholder="Enter batch number..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ fontSize: '0.875rem' }}
            />
          </InputGroup>
        </FormGroup>

        {/* Stock Table */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <div className="mt-3" style={{ fontSize: '0.875rem', color: '#6c757d' }}>
              Loading stock data...
            </div>
          </div>
        ) : (
          <>
            <Table responsive striped style={{ fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  <th style={{ fontSize: '0.875rem' }}>Select</th>
                  <th style={{ fontSize: '0.875rem' }}>Batch No</th>
                  <th style={{ fontSize: '0.875rem' }}>Expiry Date</th>
                  {showCostPriceColumn && <th style={{ fontSize: '0.875rem' }}>Cost Price</th>}
                  <th style={{ fontSize: '0.875rem' }}>Sales Price</th>
                  <th style={{ fontSize: '0.875rem' }}>Discount %</th>
                  <th style={{ fontSize: '0.875rem' }}>Status</th>
                </tr>
              </thead>
              <tbody style={{ fontSize: '0.875rem' }}>
                {getDisplayItems().map((item) => (
                  <tr key={item.purchaseStock_Id} style={{ fontSize: '0.875rem' }}>
                    <td style={{ fontSize: '0.875rem' }}>
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.purchaseStock_Id)}
                        onChange={() => handleItemToggle(item.purchaseStock_Id)}
                        disabled={lockedStockIds.includes(item.purchaseStock_Id)}
                        className="form-check-input"
                        title={lockedStockIds.includes(item.purchaseStock_Id) ? 'This stock is locked by another user' : ''}
                      />
                    </td>
                    <td style={{ fontSize: '0.875rem' }}>{item.purchaseStock_BatchNo || 'N/A'}</td>
                    <td style={{ fontSize: '0.875rem' }}>
                      <span className={isExpired(item.purchaseStock_ExpDate) ? 'text-danger' : 
                                   isCloseToExpiry(item.purchaseStock_ExpDate) ? 'text-warning' : ''}
                            style={{ fontSize: '0.875rem' }}>
                        {formatDate(item.purchaseStock_ExpDate)}
                      </span>
                    </td>
                    {showCostPriceColumn && (
                      <td style={{ fontSize: '0.875rem' }}>{(item.purchaseStock_CostPrice || 0).toFixed(2)}</td>
                    )}
                    <td style={{ fontSize: '0.875rem' }}>{(item.purchaseStock_SalesPrice || 0).toFixed(2)}</td>
                    <td style={{ fontSize: '0.875rem' }}>{(item.discountPercent || 0).toFixed(2)}%</td>
                    <td style={{ fontSize: '0.875rem' }}>
                      {isExpired(item.purchaseStock_ExpDate) && (
                        <span className="badge bg-danger" style={{ fontSize: '0.875rem', lineHeight: '1.2', padding: '0.25em 0.4em' }}>Expired</span>
                      )}
                      {!isExpired(item.purchaseStock_ExpDate) && isCloseToExpiry(item.purchaseStock_ExpDate) && (
                        <span className="badge bg-warning" style={{ fontSize: '0.875rem', lineHeight: '1.2', padding: '0.25em 0.4em' }}>Close to Expiry</span>
                      )}
                      {!isExpired(item.purchaseStock_ExpDate) && !isCloseToExpiry(item.purchaseStock_ExpDate) && (
                        <span className="badge bg-success" style={{ fontSize: '0.875rem', lineHeight: '1.2', padding: '0.25em 0.4em' }}>Good</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Show More Button - UPDATED: Now fetches next page from server */}
            {hasMorePages && (
              <div className="text-center mt-3">
                <Button 
                  color="primary" 
                  outline 
                  onClick={handleShowMore} 
                  disabled={isLoadingMore}
                  style={{ fontSize: '0.875rem' }}
                >
                  {isLoadingMore ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Loading...
                    </>
                  ) : (
                    `Load More (Page ${currentPage + 1} of ${totalPages} - ${totalRecords} total items)`
                  )}
                </Button>
                <div className="mt-2 text-muted" style={{ fontSize: '0.75rem' }}>
                  Currently showing {filteredStockItems.length} of {totalRecords} items
                </div>
              </div>
            )}

            {/* Selection Summary - UPDATED: Show pagination info */}
            {selectedItems.length > 0 && (
              <div className="mt-3 p-2 bg-light rounded" style={{ fontSize: '0.875rem' }}>
                <strong style={{ fontSize: '0.875rem' }}>Selected Items: {selectedItems.length}</strong>
                <br />
                <span className="text-muted" style={{ fontSize: '0.875rem' }}>
                  Requested: {requestedQuantity} | Selected: {selectedItems.length}
                  {totalRecords > 0 && (
                    <> | Showing: {filteredStockItems.length} of {totalRecords} available</>
                  )}
                </span>
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter style={{ fontSize: '0.875rem' }}>
        <Button color="secondary" onClick={handleCancel} style={{ fontSize: '0.875rem' }}>
          Cancel
        </Button>
        <Button 
          color="primary" 
          onClick={handleConfirm}
          disabled={selectedItems.length === 0}
          style={{ fontSize: '0.875rem' }}
        >
          Confirm Selection ({selectedItems.length} items)
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default StockSelectionModal;
